import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertOctagon,
  Clock,
  Users,
  MessageSquare,
  Shield,
  FileText,
  History,
  Target,
  AlertTriangle,
  Server,
  Activity,
  Eye,
  CheckCircle2,
  XCircle,
  BarChart3,
  Link as LinkIcon,
  Terminal
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';

interface IncidentComment {
  id: string;
  user: {
    name: string;
    avatar: string;
    role: string;
  };
  content: string;
  timestamp: string;
  attachments?: {
    name: string;
    type: string;
    url: string;
  }[];
}

interface IncidentUpdate {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  details: string;
  status?: string;
}

interface IncidentEvidence {
  id: string;
  type: string;
  source: string;
  timestamp: string;
  description: string;
  data: any;
}

interface IncidentDetail {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'investigating' | 'contained' | 'resolved';
  detectedAt: string;
  lastUpdated: string;
  description: string;
  affectedSystems: string[];
  assignedTeam: string[];
  category: string;
  source: string;
  ttd: string; // Time to detect
  ttr: string; // Time to respond
  impactScore: number;
  updates: IncidentUpdate[];
  evidence: IncidentEvidence[];
  relatedIncidents?: string[];
  mitigation?: string;
  recommendations?: string[];
  indicators?: {
    type: string;
    value: string;
    confidence: number;
  }[];
  comments: IncidentComment[];
}

const IncidentDetails = () => {
  const [newComment, setNewComment] = useState('');
  const { id } = useParams();

  // Mock data - in production, this would be fetched from an API
  const incident: IncidentDetail = {
    id: '1',
    title: 'SQL Injection Attack Detected',
    severity: 'critical',
    status: 'investigating',
    detectedAt: '2025-02-02T15:30:00Z',
    lastUpdated: '2025-02-02T17:45:00Z',
    description: 'Multiple SQL injection attempts detected targeting the customer database API endpoint. Initial analysis shows attempted data exfiltration.',
    affectedSystems: [
      'Customer Database',
      'API Gateway',
      'Authentication Service'
    ],
    assignedTeam: [
      'Security Team',
      'Database Team',
      'API Team'
    ],
    category: 'Web Application Attack',
    source: 'WAF Alerts',
    ttd: '5 minutes',
    ttr: '15 minutes',
    impactScore: 85,
    updates: [
      {
        id: 'upd-1',
        timestamp: '2025-02-02T15:35:00Z',
        user: 'Security Team',
        action: 'Investigation Started',
        details: 'Initial analysis of WAF logs and database queries in progress',
        status: 'investigating'
      },
      {
        id: 'upd-2',
        timestamp: '2025-02-02T16:00:00Z',
        user: 'Database Team',
        action: 'Containment Measures',
        details: 'Implemented additional input validation and rate limiting',
        status: 'contained'
      },
      {
        id: 'upd-3',
        timestamp: '2025-02-02T17:45:00Z',
        user: 'API Team',
        action: 'Security Patch',
        details: 'Deployed emergency patch to fix SQL injection vulnerability',
        status: 'resolved'
      }
    ],
    evidence: [
      {
        id: 'ev-1',
        type: 'Request Log',
        source: 'CloudFlare',
        timestamp: '2025-02-02T15:30:00Z',
        description: 'SQL injection pattern detected in request payload',
        data: {
          ip: '192.168.1.100',
          payload: "'; DROP TABLE users;--",
          headers: {
            'user-agent': 'Custom Bot/1.0'
          }
        }
      },
      {
        id: 'ev-2',
        type: 'Response Body',
        source: 'PostgreSQL',
        timestamp: '2025-02-02T15:30:05Z',
        description: 'Failed SQL query execution due to syntax error',
        data: {
          query: 'SELECT * FROM users WHERE id = 1 OR 1=1;',
          error: 'Syntax error in SQL statement'
        }
      }
    ],
    relatedIncidents: ['INC-2024-002', 'INC-2024-003'],
    mitigation: 'Implemented WAF rules, input validation, and prepared statements in the API endpoints',
    recommendations: [
      'Conduct thorough code review of all API endpoints',
      'Implement real-time SQL query monitoring',
      'Update security training for development team'
    ],
    indicators: [
      {
        type: 'Exposed Server IP',
        value: '192.168.1.100',
        confidence: 95
      },
      {
        type: 'System User',
        value: 'root/ubuntu',
        confidence: 85
      }
    ],
    comments: [
      {
        id: 'comment-1',
        user: {
          name: 'John Doe',
          avatar: '/avatars/john.jpg',
          role: 'Security Analyst'
        },
        content: 'Initial analysis shows the attack originated from IP 192.168.1.100',
        timestamp: '2025-02-02T15:40:00Z'
      },
      {
        id: 'comment-2',
        user: {
          name: 'Jane Smith',
          avatar: '/avatars/jane.jpg',
          role: 'Database Administrator'
        },
        content: 'I\'ve applied temporary IP blocking rules and am reviewing the affected queries',
        timestamp: '2025-02-02T16:05:00Z'
      }
    ]
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10';
      default: return 'text-blue-500 bg-blue-500/10';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-purple-500 bg-purple-500/10';
      case 'investigating': return 'text-blue-500 bg-blue-500/10';
      case 'contained': return 'text-orange-500 bg-orange-500/10';
      case 'resolved': return 'text-green-500 bg-green-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <AlertOctagon className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Incident Details</h1>
            <p className="text-sm text-gray-400">#{incident.id}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline">
            <History className="h-4 w-4 mr-2" />
            View Timeline
          </Button>
          <Button variant="outline">
            <FileText className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button>
            <Shield className="h-4 w-4 mr-2" />
            Take Action
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Key Information */}
        <div className="col-span-2 space-y-6">
          {/* Status Card */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white mb-2">{incident.title}</h2>
                <div className="flex items-center space-x-2">
                  <Badge className={getSeverityColor(incident.severity)}>
                    {incident.severity.toUpperCase()}
                  </Badge>
                  <Badge className={getStatusColor(incident.status)}>
                    {incident.status.toUpperCase()}
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-400">Impact Score</div>
                <div className="text-2xl font-bold text-red-500">{incident.impactScore}%</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-gray-400 mb-1 flex items-center">
                  <Target className="h-4 w-4 mr-1" />
                  Category
                </div>
                <div className="text-sm text-white">{incident.category}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1 flex items-center">
                  <Server className="h-4 w-4 mr-1" />
                  Source
                </div>
                <div className="text-sm text-white">{incident.source}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1 flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Detected
                </div>
                <div className="text-sm text-white">
                  {new Date(incident.detectedAt).toLocaleString()}
                </div>
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-400 mb-2">Description</div>
              <div className="text-sm text-white bg-gray-800 p-3 rounded-md">
                {incident.description}
              </div>
            </div>
          </div>

          {/* Evidence Card */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <Eye className="h-5 w-5 mr-2" />
              Evidence & Indicators
            </h2>

            {/* Evidence List */}
            <div className="space-y-4">
              {incident.evidence.map((item) => (
                <div
                  key={item.id}
                  className="bg-gray-800/30 rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Terminal className="h-4 w-4 text-blue-400" />
                      <span className="text-sm font-medium text-white">{item.type}</span>
                    </div>
                    <Badge>{item.source}</Badge>
                  </div>
                  <div className="text-sm text-gray-400">{item.description}</div>
                  <div className="bg-gray-900 p-3 rounded-md">
                    <pre className="text-xs text-gray-300 overflow-x-auto">
                      {JSON.stringify(item.data, null, 2)}
                    </pre>
                  </div>
                </div>
              ))}
            </div>

            {/* Indicators */}
            {incident.indicators && (
              <div className="space-y-3">
                <div className="text-sm text-gray-400">Threat Indicators</div>
                <div className="grid grid-cols-2 gap-4">
                  {incident.indicators.map((indicator, index) => (
                    <div
                      key={index}
                      className="bg-gray-800/30 rounded-lg p-3 flex items-center justify-between"
                    >
                      <div>
                        <div className="text-xs text-gray-400">{indicator.type}</div>
                        <div className="text-sm text-white">{indicator.value}</div>
                      </div>
                      <Badge className="bg-blue-500/10 text-blue-400">
                        {indicator.confidence}% Confidence
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="space-y-6 mt-12 py-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Discussion
                </h2>
                <span className="text-sm text-gray-400">{incident.comments?.length || 0} comments</span>
              </div>

              {/* New Comment Input */}
              <div className="flex items-start space-x-4">
                <img
                  src="https://api.dicebear.com/7.x/avataaars/svg?seed=techni"
                  alt="Current User"
                  className="w-10 h-10 rounded-full"
                />
                <div className="flex-1">
                  <textarea
                    className="w-full px-3 py-2 text-sm bg-gray-800/50 border border-gray-700/50 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    placeholder="Add a comment..."
                    rows={3}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                  />
                  <div className="mt-2 flex justify-end">
                    <Button size="sm">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Comment
                    </Button>
                  </div>
                </div>
              </div>

              {/* Comments List */}
              <div className="space-y-6">
                {incident.comments?.map((comment) => (
                  <div key={comment.id} className="flex space-x-4">
                    <img
                      src={comment.user.avatar}
                      alt={comment.user.name}
                      className="w-10 h-10 rounded-full"
                    />
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-white">
                            {comment.user.name}
                          </span>
                          <span className="text-xs text-gray-400">
                            {comment.user.role}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(comment.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300">{comment.content}</p>
                      {comment.attachments && (
                        <div className="flex items-center space-x-2">
                          {comment.attachments.map((attachment, index) => (
                            <a
                              key={index}
                              href={attachment.url}
                              className="inline-flex items-center px-3 py-1 rounded-full bg-gray-800/50 text-xs text-gray-300 hover:bg-gray-700/50 transition-colors"
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              {attachment.name}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Analysis & Updates */}
        <div className="space-y-6">
          {/* Response Metrics */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Response Metrics
            </h2>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Time to Detect</span>
                  <span className="text-sm text-white">{incident.ttd}</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: '85%' }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Time to Respond</span>
                  <span className="text-sm text-white">{incident.ttr}</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: '70%' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Affected Systems */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <Server className="h-5 w-5 mr-2" />
              Affected Systems
            </h2>
            <div className="space-y-2">
              {incident.affectedSystems.map((system, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 bg-gray-800/30 rounded-lg p-3"
                >
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm text-white">{system}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Updates Timeline */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <History className="h-5 w-5 mr-2" />
              Response Timeline
            </h2>

            <div className="space-y-4">
              {incident.updates.map((update, index) => (
                <div
                  key={update.id}
                  className="relative pl-4 pb-4 border-l border-gray-700 last:pb-0"
                >
                  <div className="absolute left-0 top-0 -translate-x-1/2 w-2 h-2 rounded-full bg-blue-500" />
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm text-white">{update.action}</div>
                    {update.status && (
                      <Badge className={getStatusColor(update.status)}>
                        {update.status}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(update.timestamp).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-300 mt-1">{update.details}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {incident.recommendations && (
            <div className="bg-[#0F1629] rounded-xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Recommendations
              </h2>
              <div className="space-y-2">
                {incident.recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-2 bg-gray-800/30 rounded-lg p-3"
                  >
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                    <span className="text-sm text-white">{rec}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IncidentDetails;
